import { useState, useEffect, useCallback } from 'react';

export interface DeviceInfo {
  isTablet: boolean;
  isPhone: boolean;
  isPortrait: boolean;
  screenWidth: number;
  screenHeight: number;
}

// Tablet threshold: width >= 768px AND height >= 600px
// This ensures landscape phones stay in compact mode
const TABLET_MIN_WIDTH = 768;
const TABLET_MIN_HEIGHT = 600;

function getDeviceInfo(): DeviceInfo {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const isTablet = width >= TABLET_MIN_WIDTH && height >= TABLET_MIN_HEIGHT;

  return {
    isTablet,
    isPhone: !isTablet,
    isPortrait: height > width,
    screenWidth: width,
    screenHeight: height,
  };
}

export function useDevice(): DeviceInfo {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(getDeviceInfo);

  const handleResize = useCallback(() => {
    setDeviceInfo(getDeviceInfo());
  }, []);

  useEffect(() => {
    // Listen to resize events
    window.addEventListener('resize', handleResize);

    // Listen to orientation changes (for mobile devices)
    window.addEventListener('orientationchange', handleResize);

    // Also handle screen.orientation API if available
    if (screen.orientation) {
      screen.orientation.addEventListener('change', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      if (screen.orientation) {
        screen.orientation.removeEventListener('change', handleResize);
      }
    };
  }, [handleResize]);

  return deviceInfo;
}
